import { useState, useCallback } from 'react'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { Button } from 'blaze-ui/Button'
import { ArrowRefresh2 } from 'blaze-ui/icons'
import { Input } from 'almanac-ui/TextInputField/Input'
import { TextFieldLoadingState } from 'components/Wizards/TextFieldLoadingState/TextFieldLoadingState'
import { usePollAICaptionGeneration } from 'hooks/queries/aiTemplates/usePollAICaptionGeneration'
import { processToolCalls } from 'components/Design/CaptionTab/processToolCalls'
import { useDebouncedCallback } from '@react-hookz/web'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { YoutubePostSettings } from 'api/clients/ScheduledPostsClient'
import { useGenerateTitle } from 'hooks/queries/youtube/useGenerateTitle'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'

interface AIFieldWithAutogenProps {
  fieldToGenerate: 'title' | 'description'
  label: string
  isTextarea?: boolean
  value: string
  setContentValues: React.Dispatch<React.SetStateAction<{ title: string; description: string }>>
  handleGenerate: (key: 'title' | 'description') => Promise<string | undefined>
  contentValues: { title: string; description: string }
  currentPostSettings: YoutubePostSettings
  updateSettings: (settings: YoutubePostSettings) => void
}

export const AIFieldWithAutogen = ({
  fieldToGenerate,
  label,
  isTextarea = false,
  value,
  setContentValues,
  handleGenerate,
  contentValues,
  currentPostSettings,
  updateSettings,
}: AIFieldWithAutogenProps) => {
  const [isDescriptionLoading, setIsDescriptionLoading] = useState(false)
  const [requestId, setRequestId] = useState<string | undefined>()

  const debouncedUpdatePost = useDebouncedCallback(
    (newValue: string) => {
      if (fieldToGenerate === 'description') {
        updateSettings({
          ...currentPostSettings,
          description: newValue,
          title: contentValues.title,
        })
      } else if (fieldToGenerate === 'title') {
        updateSettings({
          ...currentPostSettings,
          title: newValue,
          description: contentValues.description,
        })
      }
    },
    [fieldToGenerate, updateSettings, contentValues, currentPostSettings],
    1000,
  )

  const {
    generateTitle,
    isLoading: isTitleLoading,
    completionId,
    handleComplete,
  } = useGenerateTitle({
    onSuccess: (newTitle) => {
      setContentValues({ ...contentValues, title: newTitle })
      debouncedUpdatePost(newTitle)
    },
    onError: () => {
      showErrorMessageToast('Failed to generate title. Please try again.')
    },
  })

  usePollAICaptionGeneration(
    { id: requestId },
    {
      enabled: !!requestId,
      onSuccess: (res) => {
        if (res.status === 'done') {
          const captions = processToolCalls(res.data)
          const newValue = captions[0]
          setContentValues({ ...contentValues, [fieldToGenerate]: newValue })
          if (fieldToGenerate === 'description') {
            debouncedUpdatePost(newValue)
          }
          resetLoadingState()
        } else if (res.status === 'failed') {
          resetLoadingState()
        }
      },
      onError: () => resetLoadingState(),
    },
  )

  const resetLoadingState = useCallback(() => {
    setIsDescriptionLoading(false)
    setRequestId(undefined)
  }, [])

  const generateField = useCallback(async () => {
    if (fieldToGenerate === 'title') {
      generateTitle({
        source_title: currentPostSettings.title,
        channel: 'youtube',
      })
    } else {
      setIsDescriptionLoading(true)
      const generatedRequestId = await handleGenerate(fieldToGenerate)
      setRequestId(generatedRequestId)
    }
  }, [handleGenerate, fieldToGenerate, generateTitle, currentPostSettings.title])

  return (
    <>
      {completionId && <PollFieldsetCompletion fieldsetCompletionId={completionId} onComplete={handleComplete} />}
      <BaseField
        label={label}
        isRequired
        fieldGap={2}
        rightContent={
          <Button
            variant="ghost"
            size="sm"
            frontIcon={ArrowRefresh2}
            onPress={generateField}
            isDisabled={isDescriptionLoading || isTitleLoading}
          >
            Regenerate
          </Button>
        }
      >
        {isDescriptionLoading || isTitleLoading ? (
          <TextFieldLoadingState lines={isTextarea ? 4 : 1} />
        ) : (
          <Input
            name={fieldToGenerate}
            placeholder={`Pre-populated ${label.toLowerCase()} goes here`}
            value={value}
            onChange={(e) => {
              const newValue = e.currentTarget.value
              setContentValues({ ...contentValues, [fieldToGenerate]: newValue })
              debouncedUpdatePost(newValue)
            }}
            size="md"
            isTextarea={isTextarea}
            rows={isTextarea ? 4 : 1}
          />
        )}
      </BaseField>
    </>
  )
}
